import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Loader2, CheckCircle2, Mail, RefreshCw } from 'lucide-react';

export const SignupCheckEmail = ({ email, returnToParam, redirectAfter }) => {
  const navigate = useNavigate();
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [error, setError] = useState('');

  const resend = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    setError('');
    const { error: err } = await supabase.auth.resend({
      type: 'signup', email,
      options: { emailRedirectTo: `${window.location.origin}/email-confirmed?returnTo=${encodeURIComponent(redirectAfter)}` },
    });
    if (err) setError(err.message);
    else { setResendSuccess(true); setTimeout(() => setResendSuccess(false), 5000); }
    setResendLoading(false);
  };

  return (
    <div className="min-h-[100dvh] bg-[#050816] flex items-center justify-center px-4 py-12" data-testid="signup-check-email">
      <Card className="w-full max-w-md bg-[#0f1724] border-white/10 animate-in fade-in zoom-in-95 duration-300">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-[#C6A14A]/20 rounded-full flex items-center justify-center mb-4">
            <Mail className="h-8 w-8 text-[#C6A14A]" />
          </div>
          <CardTitle className="text-2xl font-light text-white">Check Your Email</CardTitle>
          <CardDescription className="text-[#E8E4DE]/80 mt-2">We&apos;ve sent a confirmation link to</CardDescription>
          <p className="text-[#C6A14A] font-medium mt-1" data-testid="signup-registered-email">{email}</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <ol className="bg-[#050816]/50 rounded-lg p-4 border border-white/5 text-[#E8E4DE]/60 text-sm space-y-2 list-decimal list-inside">
            <li>Open the email from RC Studios</li>
            <li>Tap the confirmation link</li>
            <li>You&apos;ll come right back to {returnToParam ? 'finish your booking' : 'your account'}</li>
          </ol>
          {error && (
            <div className="flex items-center gap-2 text-red-400 text-sm bg-red-400/10 p-3 rounded-lg">
              <AlertCircle className="h-4 w-4 shrink-0" /><span>{error}</span>
            </div>
          )}
          {resendSuccess && (
            <div className="flex items-center gap-2 text-green-400 text-sm bg-green-400/10 p-3 rounded-lg">
              <CheckCircle2 className="h-4 w-4 shrink-0" /><span>Confirmation email resent! Check your inbox.</span>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex flex-col gap-3">
          <Button onClick={resend} disabled={resendLoading} variant="outline" className="w-full border-white/10 text-[#E8E4DE] hover:bg-white/5 h-11" data-testid="resend-confirmation-btn">
            {resendLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</> : <><RefreshCw className="h-4 w-4 mr-2" />Resend Confirmation Email</>}
          </Button>
          <Button
            onClick={() => navigate(`/login?returnTo=${encodeURIComponent(redirectAfter)}`)}
            className="w-full bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A] font-medium h-11"
            data-testid="signup-signin-btn"
          >
            Already Confirmed? Sign In
          </Button>
          {returnToParam && <p className="text-xs text-[#E8E4DE]/50 text-center">Your order draft is saved on this device</p>}
        </CardFooter>
      </Card>
    </div>
  );
};
